import type { SkyEnvironment } from "@skypointcloud/skypoint-ui";
import { readSkyEnvironment } from "@shared/skyEnvironment";

/**
 * Build-time configuration, read once and named (CLAUDE.md "Environment
 * variables"). Reading `import.meta.env` at the point of use scatters string
 * literals that nothing checks; this module is the list.
 */

/**
 * LOCAL-DEV §3: the dev issuer replaces the hosted identity provider when this is `"1"`. There
 * are no build-time sign-in values: the browser reads its sign-in configuration from
 * `GET /api/auth/config` at runtime (`lib/auth/runtimeConfig.ts`).
 */
export const IS_DEV_AUTH = import.meta.env.VITE_DEV_AUTH === "1";

/**
 * FR-14 / SEC-14: the idle timeout in minutes when the server names none (a local `DEV_AUTH`
 * stack, whose `GET /api/auth/config` is a 404). A deployment sets it at runtime with the web's
 * `IDLE_TIMEOUT_MINUTES`, served as `idleTimeoutMinutes` (round 9, sign-out fix F); it was a
 * build-time `VITE_` value no image ever set.
 */
export const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;

/** The server's bounds on `IDLE_TIMEOUT_MINUTES` (`server/config.ts`). */
const IDLE_TIMEOUT_BOUNDS = { min: 10, max: 240 } as const;

/** A served idle timeout, held to the server's bounds; the default when it is not a number. */
export function idleTimeoutMinutesFrom(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) {
    return DEFAULT_IDLE_TIMEOUT_MINUTES;
  }
  return Math.min(IDLE_TIMEOUT_BOUNDS.max, Math.max(IDLE_TIMEOUT_BOUNDS.min, Math.floor(raw)));
}

/** Warning window before the idle logoff, in seconds. */
export const IDLE_WARNING_SECONDS = 60;

/**
 * Which Skypoint environment the app switcher launches its peers into. Read at runtime from the
 * document the web serves (`SKY_ENVIRONMENT` on the web, `shared/skyEnvironment.ts`, #864), never
 * compiled into the bundle, so one image opens each environment's own apps. No tag keeps the old
 * default: production for a production bundle, development otherwise.
 */
export function skyEnvironmentFrom(
  doc: Document | undefined,
  isProdBundle: boolean,
): SkyEnvironment {
  return readSkyEnvironment(doc) ?? (isProdBundle ? "production" : "development");
}

export const SKY_ENVIRONMENT: SkyEnvironment = skyEnvironmentFrom(
  typeof document === "undefined" ? undefined : document,
  import.meta.env.PROD,
);

/**
 * Fixes wave 3: whether the Skypoint app switcher asks skySupport which apps the person may open
 * (`GET /api/v1/uam/app-access`). Off unless the build sets `VITE_APP_SWITCHER_ACCESS=1`, because
 * skySupport does not yet allow the skyData origins (CORS) or tokens: the request failed on every
 * page and left a console error that no code can hide once the request is made. Off, the switcher
 * is the library's classic list and makes no request. docs/backlog.md has the owner-side action.
 */
export const APP_SWITCHER_ACCESS = import.meta.env.VITE_APP_SWITCHER_ACCESS === "1";

/** What this bundle was built from; each part null when the build did not stamp it. */
export interface BuildStamp {
  /** The UTC build date as `YYYY.MM.DD`; null on a local build, which reads as `dev`. */
  version: string | null;
  /** The full commit. */
  commit: string | null;
  /** The UTC build time, ISO 8601. */
  time: string | null;
}

/**
 * The build stamp the footer shows (2026-09-28, `components/shell/BuildVersion.tsx`). An image
 * built without the arguments carries each one as the empty string, which is "not stamped".
 */
export const BUILD_STAMP: BuildStamp = {
  version: import.meta.env.VITE_BUILD_VERSION?.trim() || null,
  commit: import.meta.env.VITE_BUILD_COMMIT?.trim() || null,
  time: import.meta.env.VITE_BUILD_TIME?.trim() || null,
};
